/**
 * @module app.cm
 */

import { autoinject, Disposable, ICollectionObserverSplice, BindingEngine } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import * as Mcg from 'app/ext/mcg-informed/interfaces';
import { PlanOfCare } from 'app/ext/mcg-informed/plan-of-care';
import { apiCall } from 'app/shared/lib/api/api';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { IApiActionResults, IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import * as CM from 'app/cm/models/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { CarePlanGoalWithLocalState } from 'app/cm/models/local-state-extensions';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { EventAggregator } from 'aurelia-event-aggregator';
import { McgInformedDataMethods as mcgMethods } from 'app/ext/mcg-informed/data-methods';
import { focusFirst } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { ITasksConfig, IQueuesConfig } from 'app/core/models/essette-interfaces';
import { ApiDate } from 'app/shared/models/essette-api';
import { allActionsSucceeded } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { localSort } from 'app/shared/lib/sorting';
import { DuplicateGoal } from 'app/cm/components/household/care-plan/add-goals';

export type HasInterventionsFlag = { hasInterventions: boolean };
export type GoalWithLocalStateAndInterventionFlag = CarePlanGoalWithLocalState & HasInterventionsFlag;

@autoinject()
export class AddMcgGoalsCustomElement extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    public householdCaseId: string | null = null;
    protected selectedMcgGuideline: Mcg.Guideline | null = null;
    protected mcgGuideline: Mcg.Assessment | null = null;
    protected mcgGuidelines: Mcg.Guideline[] = [];
    protected goalLookups: CM.CarePlanGoalDefinition[] = [];
    protected priorityLookups: CM.CarePlanGoalPriority[] = [];
    /** Permission that controls whether the MCG Informed integration is available */
    protected hasMcgInformed: boolean = false;
    protected caseSettings: IApiSettings = {};
    protected currentGoals: CarePlanGoalWithLocalState[] = [];
    protected allowDuplicateGoals: boolean = false;
    protected selectedGoalsSubscription: Disposable | null = null;
    protected selectedGoals: CM.CarePlanGoalDefinition[] = [];
    protected insertGoals: GoalWithLocalStateAndInterventionFlag[] = [];
    protected selectedMcgProblem: Mcg.Problem | null = null;
    protected selectedMcgGoals: Mcg.IGoal[] | null = null;
    protected selectedMcgInterventions: Mcg.IIntervention[] | null = null;
    protected insertInterventions: CM.CarePlanIntervention[] = [];
    protected customGoal: CM.CarePlanGoalDefinition | undefined = undefined;
    protected id: guid = guid();
    protected problemId: string | null = null;
    protected nextButtonEnabled: boolean = false;
    protected updateAccordionHeader: boolean = false;
    protected selectedGoalsEnabled: boolean = false;

    /** Holds cases associated with a household case */
    protected cases: CM.Case[] = [];
    protected selectedCases: CM.Case[] = [];

    protected taskConfig: ITasksConfig = {
        serviceRootUrl: 'cm/careplans',
        targetType: 'CM.CarePlanGoal',
        addTargetProperty: this.addCaseProperty.bind(this),
        primaryTargetType: 'CM.CarePlanIntervention',
        labelKey: 'cm:carePlanIntervention.$',
        allowDuplicates: false
    };

    private queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };

    ////****************** Element References ***************************************////
    protected $planOfCare!: PlanOfCare;
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $selectedGoalsli!: HTMLLIElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly carePlanService: CarePlanService,
        private readonly mcgService: McgInformedService,
        private readonly bindingEngine: BindingEngine,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly context: CasePageContext,
        private readonly caseService: CaseService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [case-id] was not set'); }

        this.caseSettings = this.context.settings;
        if(this.context.case.carePlan?.goals) {
            this.currentGoals = this.context.case.carePlan.goals! || [];
        }
        else {
            this.loadCurrentGoals(this.householdCaseId);
        }

        this.checkPermissions();
        this.allowDuplicateGoals = !!getApiSetting(this.caseSettings, 'carePlan.goals.$allowDuplicates');
        this.loadLookups();
    }

    protected attached(): void {
        this.observeSelectedTasks();

        this.events.subscribe('mcg-goal-checked-changed', (value: boolean) => {
            this.nextButtonEnabled = value;
        });

    }

    protected detached(): void {
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        let success: boolean = true;
        if(this.selectedMcgProblem && !this.problemExistsOnCareplan(this.selectedMcgProblem)) success = await this.saveSelectedProblem();
        success = success && await this.saveSelectedGoals();
        return success;
    }

    public reset(): void {
        this.selectedMcgProblem = null;
        this.selectedGoals.splice(0);
        this.selectedMcgGoals?.splice(0);
        this.selectedCases.splice(0);
        this.insertGoals.splice(0);
        this.selectedMcgInterventions?.splice(0);
        this.insertInterventions.splice(0);
        this.selectedMcgGuideline = null;
        this.mcgGuideline = null;
        this.updateAccordionHeader = false;
        this.selectedGoalsEnabled = false;

        if(this.householdCaseId) {
            this.loadCurrentGoals(this.householdCaseId);
        }

        this.problemId = null;

        if(this.$planOfCare) this.$planOfCare.reset();
        this.$collapsible.close(1);
        this.$collapsible.open(0);
    }

    private delayedFocus() {
        setTimeout(() => focusFirst(document.getElementsByTagName("add-mcg-goals")[0]), 250);
    }

    ////****************** Functions ************************************************////
    protected addCaseProperty(model: CM.CaseTask): void {
        model.case = { id: this.householdCaseId };
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasMcgInformed = this.env.config.integrations.includes('cci') && this.accountContext.hasRouteAccess('cci.assessment');
    }

    protected mcgGoalsOpened(): void {
        this.selectedGoalsEnabled = false;
        this.checkPermissions();
        this.loadLookups();
    }

    protected observeSelectedTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedGoalsSubscription) {
            this.selectedGoalsSubscription.dispose();
        }
        this.selectedGoalsSubscription = this.bindingEngine
            .collectionObserver(
                this.selectedGoals) // observe the selectedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(
                this.selectedGoalsChanged
                    .bind(this)); // pass the splice information (addedCount, index, removed) to selectedTasksChanged method

        // subscription disposed of in detached()
    }


    protected async loadLookups(): Promise<void> {
        if(!this.householdCaseId) return;

        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = {
                all: true
            };
            const caseIncludes: string[] = [
                'member'
            ]

            const [goalDefinitions, priorities, cases] = await Promise.all(
                [
                    this.carePlanService.loadGoalDefinitions(options, ['metadata.created']),
                    this.carePlanService.loadCarePlanGoalPriorities(options),
                    this.caseService.loadCasesByHouseholdCase(this.householdCaseId, options, caseIncludes)
                ]);

            this.priorityLookups = priorities.results;
            this.cases = cases.results;

            // use local sorting to sort cases by member description
            const caseSorting: Sorting = new Sorting();
            caseSorting.entries = [
                {
                    reference: 'member.description',
                    direction: SortDirection.Ascending
                }
            ];
            localSort(this.cases, caseSorting);

            if(!this.hasMcgInformed) return;

            let mcgGuidelinesResponse = await this.mcgService.loadGuidelineList();
            if(mcgGuidelinesResponse.isSuccessful) {
                this.mcgGuidelines = mcgGuidelinesResponse.result.guidelines;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected async selectedMcgGuidelineChanged(): Promise<void> {
        this.updateAccordionHeader = false;
        this.nextButtonEnabled = false;
        this.insertGoals.splice(0);

        if(!this.selectedMcgGuideline) {
            this.mcgGuideline = null;
            return;
        }
        const hsim = this.selectedMcgGuideline.Hsim;
        const edition = this.selectedMcgGuideline.Edition;
        await this.loadSelectedMcgGuideline(hsim, edition);
        this.$planOfCare.reload();
    }

    public async next(): Promise<void> {
        this.selectedGoalsEnabled = true;
        this.insertInterventions.splice(0);
        this.insertGoals.splice(0);
        this.selectedMcgProblem = await this.$planOfCare.getSelectedProblem();
        this.selectedMcgGoals = await this.$planOfCare.getSelectedGoals();
        this.selectedMcgInterventions = await this.$planOfCare.getSelectedInterventions();


        if(this.selectedMcgGoals.length > 0) {
            this.selectedMcgGoals.forEach(goal => {
                const g: GoalWithLocalStateAndInterventionFlag = {
                    isCustom: true,
                    description: mcgMethods.sanitizeText(goal),
                    definition: { id: this.customGoal?.id },
                    case: { id: this.householdCaseId },
                    _state: { isSaved: false, addToLibrary: false },
                    externalSystem: { code: 'CCI' },
                    externalIdentifier: goal.uid,
                    externalAssessmentIdentifier: this.selectedMcgGuideline?.hsim,
                    externalAssessmentVersion: this.selectedMcgGuideline?.edition,
                    hasInterventions: this.selectedMcgInterventions?.some(i => i.uid.substring(0, i.uid.lastIndexOf('-')) === goal.uid) || false
                };
                this.insertGoals.push(g);
            });
        }

        if(this.selectedMcgInterventions.length > 0) {
            for(const intervention of this.selectedMcgInterventions) {
                if(this.$planOfCare.interventionExistsOnCareplan(intervention)) continue;

                const i: CM.CarePlanIntervention = {
                    isCustom: true,
                    description: mcgMethods.sanitizeText(intervention),
                    delegated: { queue: null },
                    externalSystem: { code: 'CCI' },
                    externalIdentifier: intervention.uid,
                    summary: {
                        mediaType: { code: 'mcg.annotation' },
                        taskSummary: intervention.text
                    }
                };
                this.insertInterventions.push(i);
            }
        }
        this.$selectedGoalsli.classList.remove('disabled');
        this.$collapsible.close(0);
        this.$collapsible.open(1);
        this.updateAccordionHeader = true;
        this.delayedFocus();
    }

    /** Called when a user removes a goal from the grid. Will ultimately trigger the selectedGoalsChanged() method */
    protected removeGoal(goal: GoalWithLocalStateAndInterventionFlag): void {
        if(goal.isCustom) {
            // Custom tasks are not entries in selectedTasks, so no need to find and remove it
            const index = this.insertGoals.indexOf(goal);
            if(index >= 0) {
                this.insertGoals.splice(index, 1);
            }
        }
        else {
            // Find the task in the selectedTasks array and remove it. This will trigger the selectedTasksChanged(), which will update insertTasks
            const st = this.selectedGoals.find(t => t.id === (goal.definition && goal.definition.id));
            if(st) {
                this.selectedGoals.splice(this.selectedGoals.indexOf(st), 1);
            }
        }
    }

    protected goalExistsOnCareplan(goal: CarePlanGoalWithLocalState): boolean {
        for(const cg of this.currentGoals) {
            if(cg.externalIdentifier === goal.externalIdentifier) return true;
        }

        return false;
    }

    protected problemExistsOnCareplan(problem: Mcg.Problem): boolean {
        let exists: boolean = false;

        for(const cg of this.currentGoals) {
            if(cg.problem?.externalIdentifier === problem.uid) {
                exists = true;
                if(cg.problem.id) this.problemId = cg.problem.id;
                break;
            }
        }

        return exists;
    }

    protected buildSaveProblemWithCaseData(memberCase: CM.Case): CM.CarePlanProblem {
        const saveProblem: CM.CarePlanProblem = {
            description: this.selectedMcgProblem?.text,
            isCustom: true,
            case: getReference(memberCase)!,
            externalSystem: { code: 'CCI' },
            externalIdentifier: this.selectedMcgProblem?.uid
        };

        return saveProblem;
    }

    /** Takes a goal from the insertGoals grid and constructs a new copy to be sent to the API */
    protected buildSaveGoalWithCaseData(memberCase: CM.Case, goal: CarePlanGoalWithLocalState): CarePlanGoalWithLocalState {
        if(!goal._state) { goal._state = {}; }

        let saveGoal: CarePlanGoalWithLocalState = {
            case: getReference(memberCase)!,
            isCustom: goal.isCustom,
            priority: getReference(goal.priority) || undefined,
            targetDate: goal.targetDate,
            definition: (!goal.isCustom && getReference(goal.definition)) || undefined,
            description: (!goal.definition && goal.description) || (goal.externalSystem && goal.description) || undefined,
            externalSystem: goal.externalSystem,
            externalIdentifier: goal.externalIdentifier
        };

        if(goal.externalSystem?.code === 'CCI' && this.selectedMcgGuideline) {
            saveGoal.externalAssessmentIdentifier = this.selectedMcgGuideline.Hsim;
            saveGoal.externalAssessmentVersion = this.selectedMcgGuideline.Edition;
        }

        return saveGoal;
    }

    protected getSelectedInterventions(externalGoalIdentifier: string): CM.CarePlanIntervention[] {
        return this.insertInterventions.filter(i => {
            const goalInterventions = this.selectedMcgGoals?.find(g => g.uid == externalGoalIdentifier)?.interventionsSet;
            return goalInterventions?.find(gi => gi.uid == i.externalIdentifier);
        });
    }

    protected copyPriority(priority: CM.CarePlanGoalPriority | null): void {
        this.insertGoals.forEach(g => {
            g.priority = priority;
        });
    }

    protected copyTargetDate(date: ApiDate | null): void {
        this.insertGoals.forEach(g => {
            g.targetDate = date;
        });
    }

    ////****************** Events ***************************************************////
    protected selectedGoalsChanged(splices: ICollectionObserverSplice<CM.CarePlanGoalDefinition>[]): void {
        if(this.insertGoals.length === 0 && this.selectedGoals.length === 0) return;

        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var goalsAdded = this.selectedGoals.slice(splice.index, splice.index + splice.addedCount);
            if(goalsAdded.length > 0) {

                goalsAdded.forEach(
                    goal => {
                        const g: GoalWithLocalStateAndInterventionFlag = {
                            isCustom: false,
                            description: goal.description,
                            definition: { id: goal.id },
                            case: { id: this.householdCaseId },
                            _state: { isSaved: false, addToLibrary: false },
                            hasInterventions: false
                        };
                        this.insertGoals.push(g);
                    });
            }

            if(splice.removed.length) {
                (splice.removed as CM.CarePlanGoalDefinition[]).forEach(
                    goal => {
                        let i = this.insertGoals.find(g => (g.definition && g.definition.id) === goal.id);
                        if(i) {
                            this.insertGoals.splice(this.insertGoals.indexOf(i), 1);
                        }
                    });
            }
        }
    }

    /** Takes a goal from the insertGoals grid and constructs a new copy to be sent to the API */
    protected buildSaveGoal(goal: CarePlanGoalWithLocalState): CarePlanGoalWithLocalState {
        if(!goal._state) { goal._state = {}; }

        let saveGoal: CarePlanGoalWithLocalState = {
            case: { id: this.householdCaseId },
            isCustom: goal.isCustom,
            priority: getReference(goal.priority) || undefined,
            targetDate: goal.targetDate,
            definition: (!goal.isCustom && getReference(goal.definition)) || undefined,
            description: (!goal.definition && goal.description) || (goal.externalSystem && goal.description) || undefined,
            externalSystem: goal.externalSystem,
            externalIdentifier: goal.externalIdentifier
        };

        if(goal.externalSystem?.code === 'CCI' && this.selectedMcgGuideline) {
            saveGoal.externalAssessmentIdentifier = this.selectedMcgGuideline.Hsim;
            saveGoal.externalAssessmentVersion = this.selectedMcgGuideline.Edition;
        }

        return saveGoal;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSelectedMcgGuideline(hsim: string, edition: string): Promise<boolean> {
        try {
            this.state.beginLoad();
            const response = await this.mcgService.getGuidelineContent(hsim, edition);

            if(response.isSuccessful) {
                this.mcgGuideline = response.result;
            }
            return response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCurrentGoals(householdCaseId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes: string[] = ['externalSystem', 'problem'];

        try {
            this.state.beginLoad();
            const response = await this.carePlanService.loadGoalsByHouseholdCase(householdCaseId, options, includes);
            this.currentGoals = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveSelectedProblem(): Promise<boolean> {
        const problemsForApi: CM.CarePlanProblem[] = [];

        this.selectedCases.forEach(c => {
            problemsForApi.push(this.buildSaveProblemWithCaseData(c))
        });

        let success = false;
        try {
            this.state.beginSave();

            const progressToast = this.toaster.standard.batch('cm:carePlanProblem.$long', problemsForApi.length);
            progressToast.show();

            const responses = await this.carePlanService.insertCarePlanProblemsBatch(problemsForApi, progressToast);
            success = allActionsSucceeded(responses);

            return Promise.resolve(success);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanProblem.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    /** Iterates through the goals and saves each one. */
    @apiCall
    private async saveSelectedGoals(): Promise<boolean> {
        const duplicateGoals: DuplicateGoal[] = [];
        const goalsForApi: CarePlanGoalWithLocalState[] = [];

        this.selectedCases.forEach(c => {
            this.insertGoals.filter(g => {
                // If user clicks save a second time, we skip goals that might have been saved already.
                return !(g._state && g._state.isSaved);
            }).forEach(g => {
                // Check for duplicate goals
                if(this.currentGoals.length > 0 && this.currentGoals.some(cg =>
                    ((cg.externalSystem?.code === 'CCI' && cg.externalIdentifier === g.externalIdentifier) ||
                        (cg.definition && cg.definition.id) === (g.definition && g.definition.id)) &&
                    (cg.member && cg.member.id) === (c.member && c.member.id))) {
                    duplicateGoals.push({ memberDescription: c.member?.description || '', goalDescription: g.description || '' });
                }

                goalsForApi.push(this.buildSaveGoalWithCaseData(c, g));
            })
        });

        /** Show confirmation to add duplicate goals */
        if(duplicateGoals.length > 0) {
            const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateGoals.length, what: '$t(cm:carePlanGoal.$)' });
            const result = await confirmation.showCustom(message, duplicateGoals.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        let success = false;
        try {
            this.state.beginSave();
            let mcgGoalsAreSuccessful = true;

            // Verify if goals are for external system and if interventions will need to be inserted as well.
            for(const g of goalsForApi.filter(goal => goal.externalSystem)) {
                if(this.problemId) g.problem = { id: this.problemId };
                let mcgResponse = await this.carePlanService.insertCarePlanGoal(g);
                if(mcgResponse.isSuccessful) g.id = mcgResponse?.id;

                // Insert Interventions
                if(g.id && this.insertInterventions.length > 0) await this.saveSelectedInterventions(g.id, g.externalIdentifier!);
                // If no goal id is obtained, the mcg goals update is unsuccessful.
                if(!g.id) {
                    mcgGoalsAreSuccessful = false;
                }
            }

            const batchGoalsForApi = goalsForApi.filter(goal => !goal.externalSystem);
            const progressToast = this.toaster.standard.batch('cm:carePlanGoal.$', batchGoalsForApi.length);
            progressToast.show();

            const responses = await this.carePlanService.insertCarePlanGoalsToCasesBatch(batchGoalsForApi, progressToast);
            success = allActionsSucceeded(responses);

            if(!success || !mcgGoalsAreSuccessful) {
                throw new Error('Goals did not save successfully');
            }

            return Promise.resolve(success && mcgGoalsAreSuccessful);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanGoal.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async saveSelectedInterventions(goalId: string, externalGoalIdentifier: string): Promise<Boolean> {
        const mcgGoal: Mcg.IGoal | undefined = this.selectedMcgGoals?.find(g => g.uid === externalGoalIdentifier);
        if(!mcgGoal) return Promise.resolve(false);

        const interventions: CM.CarePlanIntervention[] = [];
        this.insertInterventions.filter(i => mcgGoal?.interventionsSet.some(gi => gi.uid === i.externalIdentifier)).forEach(i => {
            let intervention: CM.CarePlanIntervention = {
                goal: { id: goalId },
                isCustom: i.isCustom,
                description: i.description,
                due: i.due,
                externalSystem: i.externalSystem,
                externalIdentifier: i.externalIdentifier,
                summary: {
                    mediaType: { code: 'mcg.annotation' },
                    taskSummary: i.summary?.taskSummary
                }
            };

            if(i.delegated && i.delegated.queue) {
                intervention.delegated = {
                    queue: getReference(i.delegated.queue),
                    to: i.delegated.to && i.delegated.queue?.delegated ? {
                        id: i.delegated.to.id,
                        type: i.delegated.queue.delegated.type
                    } : undefined
                };
            }

            interventions.push(intervention);
        });

        let success: boolean = false;
        try {
            this.state.beginSave();

            const progressToast = this.toaster.standard.batch('cm:carePlanIntervention.$long', interventions.length);
            progressToast.show();

            const responses = await this.carePlanService.insertCarePlanInterventionsBatch(interventions, progressToast);
            success = allActionsSucceeded(responses);

            return Promise.resolve(success);
        } catch(error) {
            this.toaster.standard.saved('cm:carePlanIntervention.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

}
